import { useCallback, useEffect, useRef, useState } from "react";
import { Request } from "@xt/xtbase";
import { get_kline } from "@/api/v4/market";

const { XtSocketPublic } = Request;

export const PC_SPARKLINE_INTERVAL = "3m";
export const PC_SPARKLINE_LIMIT = 480;

const chartCache = {};
const wsRefCount = {};

export function resolveKlineSymbol(displaySymbol) {
  const core = String(displaySymbol || "")
    .toLowerCase()
    .replace(/_cfd$/i, "");
  return core ? `${core}_cfd` : "";
}

function mergeWsKline(existing, res) {
  if (!existing?.length) return [res];
  const list = [];
  let replaced = false;
  existing.forEach((bar) => {
    if (bar.t === res.t) {
      list.push(res);
      replaced = true;
    } else {
      list.push(bar);
    }
  });
  if (!replaced) list.unshift(res);
  return list;
}

function subscribeKline(sym, onUpdate) {
  wsRefCount[sym] = (wsRefCount[sym] || 0) + 1;
  if (wsRefCount[sym] > 1) return;

  const event = `kline@${sym},${PC_SPARKLINE_INTERVAL}`;
  XtSocketPublic.addChannel({ event }, (res) => {
    if (res?.s !== sym) return;
    chartCache[sym] = mergeWsKline(chartCache[sym], res);
    onUpdate();
  });
}

function unsubscribeKline(sym) {
  if (!wsRefCount[sym]) return;
  wsRefCount[sym] -= 1;
  if (wsRefCount[sym] > 0) return;
  delete wsRefCount[sym];
  XtSocketPublic.removeChannel({ event: `kline@${sym},${PC_SPARKLINE_INTERVAL}` });
}

/** 对齐 MarketCard：3m / 480 + WS 增量更新 */
export function usePcSparklines(symbols, marketType, enabled = true) {
  const [version, setVersion] = useState(0);
  const activeRef = useRef([]);

  const bump = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;

    const next = [...new Set((symbols || []).map((s) => resolveKlineSymbol(s)).filter(Boolean))];
    const prev = activeRef.current;

    prev.forEach((sym) => {
      if (!next.includes(sym)) unsubscribeKline(sym);
    });

    next.forEach((sym) => {
      subscribeKline(sym, bump);

      if (chartCache[sym]?.length) return;

      get_kline({
        params: {
          symbol: sym,
          interval: PC_SPARKLINE_INTERVAL,
          limit: PC_SPARKLINE_LIMIT,
          version: 2,
        },
        isAutoPopError: false,
      }).then((data) => {
        if (data?.length) {
          chartCache[sym] = data;
          bump();
        }
      });
    });

    activeRef.current = next;

    return () => {
      next.forEach((sym) => unsubscribeKline(sym));
      activeRef.current = [];
    };
  }, [enabled, marketType, (symbols || []).join(","), bump]);

  const getChartData = useCallback(
    (displaySymbol) => {
      const klineSym = resolveKlineSymbol(displaySymbol);
      return chartCache[klineSym] || null;
    },
    [version]
  );

  return { getChartData };
}
